import { AlertTriangle, CheckCircle2, CloudOff, ShieldAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { useSiteStore, useSnapshot } from "@/store/site";
import { useAlarms } from "@/hooks/useAlarms";
import { useWorstAlarm } from "@/hooks/useWorstAlarm";
import { carryTo } from "@/lib/carryParams";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";
import { formatEur, formatRelativeAge, formatTimeOfDay } from "@/lib/format";
import { useDischargePrice } from "@/hooks/useDischargePrice";

export function HealthBanner() {
  const snap = useSnapshot();
  const mode = useSiteStore((s) => s.mode);
  const connection = useSiteStore((s) => s.connection);
  const t = useT();
  // Shared alarms cache — see useAlarms() for why this is no longer a
  // per-component 5 s setInterval (it was a contributor to the Overview's
  // 5 s animation stutter). `alarms` is still read directly for the
  // acknowledged-count below; worst-alarm selection is delegated to the
  // shared useWorstAlarm() hook (F24) so the banner and the shell-level
  // IncidentStrip cite the same incident.
  const { data: alarms = [] } = useAlarms();
  const { isError, active, criticalCount, top } = useWorstAlarm();

  // ONE money clause on the sub-line (F08). Mirrors StoryCards.tsx EXACTLY
  // so both figures use one tariff: discharge_kWh × the operator-set
  // discharge price. Gated on a real, positive price and finite kWh — never
  // render a fabricated €0 (the headline must stay honest; the figure is
  // still one tap away on the Overview StoryCards just below).
  const { price: dischargePrice, isReady: priceReady } = useDischargePrice();
  const discharge_kWh = Number.isFinite(snap.energy_today.bess_discharge_kWh) ? snap.energy_today.bess_discharge_kWh : NaN;
  const moneyKnown = priceReady && dischargePrice > 0 && Number.isFinite(discharge_kWh);
  const moneySaved = moneyKnown ? discharge_kWh * dischargePrice : 0;
  const moneyClause = moneyKnown ? (
    <Link
      to={carryTo("/revenue")}
      className="hidden shrink-0 items-center text-sm text-text-muted hover:text-accent sm:flex"
      title={t("monitor.health.moneyClauseTitle")}
    >
      {t("monitor.health.moneyClause", { eur: formatEur(moneySaved, 0) })}
    </Link>
  ) : null;

  // Truthful sub-line (DATA-03). The TB assembler never populates
  // services_active/services_total/last_uplink_age_s (hardcoded 0/0/0), so
  // in live mode the old line read "0/0 services active · cloud uplink
  // fresh (0 s)" — fabricated. Show facts the adapter can actually source:
  // newest snapshot age, online BESS count, and the store connection word.
  // Mock keeps the services line — its generator genuinely maintains the
  // counters, and the demo should keep demonstrating them.
  // {conn} stays the raw store token (wire value — untranslated).
  const subline = mode !== "mock"
    ? t("monitor.health.subline.live", {
        time: formatTimeOfDay(snap.ts),
        n: snap.bess_aggregate.online_count,
        m: snap.bess_per_unit.length,
        conn: connection,
      })
    : t("monitor.health.subline.mock", {
        a: snap.health.services_active,
        b: snap.health.services_total,
        s: Math.round(snap.health.last_uplink_age_s),
      });

  // Alarms unreachable ⇒ health is UNKNOWN — render neutral/warn, never
  // green (DATA-03): a connectivity outage must not present as good news.
  if (isError) {
    return (
      <div className="health-banner degraded" aria-busy="true">
        <span className="hb-pip" />
        <div className="flex-1">
          <div className="font-semibold" role="status" aria-live="polite" aria-atomic="true">{t("monitor.health.unknown")}</div>
          <div className="text-sm text-text-muted">{subline}</div>
        </div>
        <CloudOff className="text-warn" size={18} />
      </div>
    );
  }

  const overall = snap.health.overall;

  if (active.length === 0 && overall === "ok") {
    return (
      <div className="health-banner">
        <span className="hb-pip" />
        <div className="flex-1">
          <div className="font-semibold" role="status" aria-live="polite" aria-atomic="true">{t("monitor.health.healthy")}</div>
          <div className="text-text-muted text-sm">
            {subline}
            {/* N04 — surface the money figure at narrow widths only. The
                standalone right-aligned moneyClause Link below is `hidden
                sm:flex`, so it vanishes exactly on the ~360px TB column the GM
                glances at. Append the same figure (identical tariff) to the
                healthy sub-line and show it only below sm so it never doubles
                the sm+ clause. Money stays off the sub-line in degraded/critical
                states, where the incident text must lead. */}
            {moneyKnown && (
              <span className="sm:hidden"> · {t("monitor.health.moneyClauseShort", { eur: formatEur(moneySaved, 0) })}</span>
            )}
          </div>
        </div>
        {moneyClause}
        <CheckCircle2 className="text-ok" size={18} />
      </div>
    );
  }

  const tone = overall === "critical" ? "critical" : "degraded";
  // Degraded with ZERO active alarms (OVW-03) — routinely reachable when a
  // critical is acknowledged-but-not-cleared, or when the EMS-published
  // severity attribute and the TB alarm list disagree. The old headline
  // read "0 critical alarms · operator action needed" and its CTA landed
  // on an empty "active" tab. Say what is actually known and point the
  // CTA at the full list instead.
  const zeroActive = active.length === 0;
  const ackCount = alarms.filter((a) => a.status === "acknowledged").length;
  const headline = zeroActive
    ? ackCount > 0
      ? t(ackCount === 1 ? "monitor.health.degradedAck.one" : "monitor.health.degradedAck.other", { n: ackCount })
      : t("monitor.health.degradedReported")
    : overall === "critical" && criticalCount > 0
      ? t(criticalCount === 1 ? "monitor.health.critical.one" : "monitor.health.critical.other", { n: criticalCount })
      : t(active.length === 1 ? "monitor.health.attention.one" : "monitor.health.attention.other", { n: active.length });
  const Icon = overall === "critical" ? ShieldAlert : AlertTriangle;

  return (
    <div className={cn("health-banner", tone)}>
      <span className="hb-pip" />
      <div className="flex-1">
        <div className="font-semibold" role="status" aria-live="polite" aria-atomic="true">{headline}</div>
        {top ? (
          <div className="text-sm text-text-muted">
            {top.device_name} · <span className="text-text">{top.message}</span> · {t("monitor.alarm.raised", { age: formatRelativeAge(Date.now() - top.raised_at) })}
          </div>
        ) : (
          // No active alarm to cite — keep the data-age facts on screen
          // exactly when degraded (OVW-03).
          <div className="text-sm text-text-muted">{subline}</div>
        )}
        {/* N04 / R10 — a degraded-but-earning site must still show the money
            answer on the ~360px TB column, where the right-aligned moneyClause
            Link below is `hidden sm:flex`. A second `sm:hidden` muted line
            INSIDE this `.flex-1` column (after the alarm sub-line, so the
            incident text leads) surfaces the same figure, identical tariff.
            Gated on moneyKnown so a degraded-and-not-earning site stays honest
            (no fabricated €0). */}
        {moneyKnown && (
          <div className="sm:hidden text-sm text-text-muted">
            {t("monitor.health.moneyClauseShort", { eur: formatEur(moneySaved, 0) })}
          </div>
        )}
      </div>
      {moneyClause}
      <Link
        // A "N critical alarms" CTA lands on the console pre-filtered to
        // criticals instead of the unfiltered list (ALM-02, LINK-09);
        // zero-active degradation opens the ALL tab, where the
        // acknowledged-but-open alarms actually are (OVW-03).
        to={carryTo(
          "/alarms",
          zeroActive ? { tab: "all" } : criticalCount > 0 ? { severity: "critical" } : undefined,
        )}
        className={cn(
          "flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider",
          overall === "critical" ? "border-critical/40 bg-critical/15 text-critical hover:bg-critical/25" : "border-warn/40 bg-warn/15 text-warn hover:bg-warn/25",
        )}
      >
        <Icon size={14} /> {zeroActive ? t("monitor.health.allAlarms") : t("common.openAlarms")}
      </Link>
    </div>
  );
}
